// src/views/pages/booking-engine/admin/PropertyContentEditor.jsx
import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";

import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Tabs,
  Tab,
  Badge,
  Table,
  Modal,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  FaInfoCircle,
  FaGavel,
  FaConciergeBell,
  FaMapMarkerAlt,
  FaHandshake,
  FaImages,
  FaEye,
  FaEdit,
  FaTrashAlt,
  FaLandmark,
  FaShieldAlt,
  FaBuilding,
  FaCheckCircle,
} from "react-icons/fa";
import AttractionsEditor from "./AttractionsEditor";
import PrivacyPolicyEditor from "./PrivacyPolicyEditor";

// =============================
// DEFAULT CONFIGS
// =============================

const DEFAULT_POLICIES = [
  {
    key: "cancellation",
    label: "Cancellation Policy",
    defaultDescription:
      "<ul><li>Free cancellation up to 48 hours before check-in.</li><li>Late cancellations or no-shows may be charged the first night.</li></ul>",
  },
  {
    key: "checkInOut",
    label: "Check-in & Check-out",
    defaultDescription:
      "<ul><li>Check-in from 2:00 PM.</li><li>Check-out by 11:00 AM.</li><li>Early check-in or late check-out is subject to availability and additional charges.</li></ul>",
  },
  {
    key: "children",
    label: "Children & Extra Beds",
    defaultDescription:
      "<ul><li>Children of all ages are welcome.</li><li>Extra beds and cribs are available on request and subject to availability.</li><li>Additional charges may apply.</li></ul>",
  },
  {
    key: "pets",
    label: "Pet Policy",
    defaultDescription:
      "<ul><li>Pets are not allowed unless explicitly confirmed at the time of booking.</li></ul>",
  },
  {
    key: "smoking",
    label: "Smoking Policy",
    defaultDescription:
      "<ul><li>Smoking is not permitted inside the rooms.</li><li>Designated smoking areas may be available within the property.</li></ul>",
  },
  {
    key: "idProof",
    label: "ID Proof Required",
    defaultDescription:
      "<ul><li>Valid government-issued photo ID is required at check-in for all guests.</li></ul>",
  },
  {
    key: "payment",
    label: "Payment & Deposit",
    defaultDescription:
      "<ul><li>We accept major credit/debit cards and selected digital wallets.</li><li>A pre-authorization or deposit may be taken at check-in.</li></ul>",
  },
  {
    key: "damage",
    label: "Damage & Missing Items",
    defaultDescription:
      "<ul><li>Any damage to rooms or missing items may be charged to the guest.</li><li>Please report any issues to the front desk immediately.</li></ul>",
  },
  {
    key: "houseRules",
    label: "House Rules",
    defaultDescription:
      "<ul><li>Parties or events are not allowed unless pre-approved.</li><li>Quiet hours may apply during the night.</li></ul>",
  },
  {
    key: "taxes",
    label: "Taxes & Fees",
    defaultDescription:
      "<ul><li>All applicable taxes and service charges will be added as per local regulations.</li></ul>",
  },
  {
    key: "parkingPolicy",
    label: "Parking Policy",
    defaultDescription:
      "<ul><li>Parking is subject to availability.</li><li>Charges may apply for reserved or valet parking.</li></ul>",
  },
];

const DEFAULT_AMENITIES = [
  { key: "wifi", label: "High-speed Wi-Fi", defaultDescription: "Complimentary Wi-Fi available in rooms and common areas." },
  { key: "parking", label: "On-site Parking", defaultDescription: "On-site parking available (subject to availability). Charges may apply." },
  { key: "pool", label: "Swimming Pool", defaultDescription: "Swimming pool access available during operating hours." },
  { key: "gym", label: "Fitness Center", defaultDescription: "Fitness center available for in-house guests." },
  { key: "restaurant", label: "Restaurant / Café", defaultDescription: "On-site restaurant/café serving breakfast and selected meals." },
  { key: "bar", label: "Bar / Lounge", defaultDescription: "Bar/lounge available for guests (as per timings)." },
  { key: "spa", label: "Spa & Wellness", defaultDescription: "Spa/wellness services available by appointment." },
  { key: "roomService", label: "24/7 Room Service", defaultDescription: "Room service available (menu/timings may apply)." },
  { key: "airport", label: "Airport Transfer", defaultDescription: "Airport pickup/drop available on request (charges may apply)." },
  { key: "reception", label: "24/7 Front Desk", defaultDescription: "Front desk available 24/7 for assistance and check-in support." },
  { key: "laundry", label: "Laundry Service", defaultDescription: "Laundry/pressing service available (charges may apply)." },
  { key: "ac", label: "Air Conditioning", defaultDescription: "Air-conditioned rooms and common areas." },
  { key: "tv", label: "Smart TV", defaultDescription: "Smart TV available in rooms." },
  { key: "teaCoffee", label: "Tea & Coffee Maker", defaultDescription: "Tea/coffee setup available in rooms." },
  { key: "miniBar", label: "Mini Bar", defaultDescription: "Mini bar available in rooms (chargeable items may apply)." },
  { key: "businessCenter", label: "Business Center", defaultDescription: "Business center facilities available for guests." },
];


const DEFAULT_PARTNERS = [
  {
    key: "booking",
    name: "Booking.com",
    url: "https://www.booking.com",
    logoUrl: "",
  },
  {
    key: "agoda",
    name: "Agoda",
    url: "https://www.agoda.com",
    logoUrl: "",
  },
  {
    key: "mmt",
    name: "MakeMyTrip",
    url: "https://www.makemytrip.com",
    logoUrl: "",
  },
  {
    key: "expedia",
    name: "Expedia",
    url: "https://www.expedia.com",
    logoUrl: "",
  },
  {
    key: "airbnb",
    name: "Airbnb",
    url: "https://www.airbnb.com",
    logoUrl: "",
  },
];

const apiUrl =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/hms";
const token = localStorage.getItem("token");
const CDN_URL = (process.env.REACT_APP_CDN_URL || "").replace(/\/$/, "");

const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

const createDefaultPoliciesSection = () => ({
  enabled: true,
  title: "Policies",
  items: [],
});

const createDefaultAmenitiesSection = () => ({
  enabled: true,
  title: "Amenities & Facilities",
  items: [],
});

const createDefaultContactSection = () => ({
  enabled: true,
  title: "Contact & Location",
  phone: "",
  email: "",
  whatsapp: "",
  website: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  country: "",
  postcode: "",
  latitude: "",
  longitude: "",
  mapEmbedUrl: "",
});

const createDefaultPartnersSection = () => ({
  enabled: false,
  title: "Trusted Partners",
  partners: [],
});

const createDefaultGallerySection = () => ({
  enabled: true,
  title: "Gallery",
  subtitle: "",
  images: [],
});

// =============================
// MAIN EDITOR
// =============================

/**
 * Props:
 * - branding
 * - setBranding(prev => newBranding)
 * - cdnBaseUrl: base CDN url
 * - heroImageKey
 * - setHeroImageKey
 * - galleryImages (legacy, optional)
 * - onSave: () => void
 */
const PropertyContentEditor = ({
  branding,
  setBranding,
  cdnBaseUrl = "",
  heroImageKey = "",
  setHeroImageKey,
  galleryImages = [],
  onSave,
}) => {
  const [showHtmlName, setShowHtmlName] = useState(false);
  const [showHtmlOverview, setShowHtmlOverview] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [deletingHero, setDeletingHero] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryDeleting, setGalleryDeleting] = useState({});

  // =============================
  // NEW: Multi-Property Selection State
  // =============================
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(true);
  const [organizationsError, setOrganizationsError] = useState("");
  const currentCompanyId = localStorage.getItem("companyid");

  // Fetch organizations on mount
  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    setLoadingOrganizations(true);
    setOrganizationsError("");
    try {
      const response = await axios.get(
        `${apiUrl}/company/organizations`,
        authHeaders
      );
      const orgs = response.data?.organizations || response.data || [];
      setOrganizations(Array.isArray(orgs) ? orgs : []);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      setOrganizationsError(
        "Failed to load properties. Please refresh the page or try again later."
      );
      setOrganizations([]);
    } finally {
      setLoadingOrganizations(false);
    }
  };

  // Get currently selected properties from branding
  const selectedProperties = branding.selectedProperties || [];

  // Check if a property is selected
  const isPropertySelected = (companyId) => {
    return selectedProperties.some(
      (p) => String(p.company_id) === String(companyId)
    );
  };

  // Toggle property selection
  const togglePropertySelection = (org) => {
    const companyId = org.company_id;
    const isSelected = isPropertySelected(companyId);

    setBranding((prev) => {
      const currentSelected = prev.selectedProperties || [];

      if (isSelected) {
        // Remove from selection
        return {
          ...prev,
          selectedProperties: currentSelected.filter(
            (p) => String(p.company_id) !== String(companyId)
          ),
        };
      } else {
        // Add to selection
        return {
          ...prev,
          selectedProperties: [
            ...currentSelected,
            {
              company_id: org.company_id,
              company_name: org.company_name,
            },
          ],
        };
      }
    });
  };

  // Select all properties
  const selectAllProperties = () => {
    setBranding((prev) => ({
      ...prev,
      selectedProperties: organizations.map((org) => ({
        company_id: org.company_id,
        company_name: org.company_name,
      })),
    }));
  };

  // Deselect all properties
  const deselectAllProperties = () => {
    setBranding((prev) => ({
      ...prev,
      selectedProperties: [],
    }));
  };

  // amenities
  const [selectedDefaultAmenityKey, setSelectedDefaultAmenityKey] =
    useState("");
  const [newAmenityName, setNewAmenityName] = useState("");
// ✅ Amenity editor modal
const [editingAmenityKey, setEditingAmenityKey] = useState(null);
const [showAmenityEditModal, setShowAmenityEditModal] = useState(false);
const [amenityDraft, setAmenityDraft] = useState({ label: "", description: "" });

  // policies
  const [selectedDefaultPolicyKey, setSelectedDefaultPolicyKey] =
    useState("");
  const [newPolicyName, setNewPolicyName] = useState("");
const [editingPolicyKey, setEditingPolicyKey] = useState(null);
// ✅ Policy editor modal
const [showPolicyEditModal, setShowPolicyEditModal] = useState(false);
const [policyDraftHtml, setPolicyDraftHtml] = useState("");

  // partners
  const [selectedDefaultPartnerKey, setSelectedDefaultPartnerKey] =
    useState("");
  const [newPartnerName, setNewPartnerName] = useState("");

  // gallery UX state
  const [previewImageUrl, setPreviewImageUrl] = useState("");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showGalleryAddModal, setShowGalleryAddModal] = useState(false);
  const [draftImage, setDraftImage] = useState({
    title: "",
    key: "",
    alt: "",
    tag: "",
    file: null,
    uploading: false,
    previewUrl: "",
    error: "",
  });
  













  
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    key: null,
    idx: null,
  });

  const [editImage, setEditImage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);



  const isAbsUrl = (u) => /^https?:\/\//i.test(u) || String(u).startsWith("//");

const resolveUrl = (p, cdnUrl = "") => {
  if (!p) return "";
  const s = String(p);

  if (isAbsUrl(s)) return s;

  const base = String(cdnUrl || "").replace(/\/$/, "");
  if (!base) {
    // ensure root-relative for things like "company_96/..."
    return s.startsWith("/") ? s : `/${s}`;
  }
  // prefix CDN
  return `${base}/${s.replace(/^\//, "")}`;
};
  // Derived gallery list (sorted)
const realGalleryImages = (() => {
  const raw =
    (branding?.gallery?.images && branding.gallery.images.length
      ? branding.gallery.images
      : null) ||
    (branding?.gallery_images && branding.gallery_images.length
      ? branding.gallery_images
      : null) ||
    (branding?.gallery?.gallery_images && branding.gallery.gallery_images.length
      ? branding.gallery.gallery_images
      : null) ||
    (galleryImages && galleryImages.length ? galleryImages : null) ||
    [];

  // Normalize strings -> objects, and ensure every item has a `key` + `order`
  const normalized = (Array.isArray(raw) ? raw : []).map((x, idx) => {
    if (typeof x === "string") {
      return { key: x, title: "", alt: "", tag: "", order: idx };
    }
    const key = x.key || x.url || x.path || x.image || "";
    return { ...x, key, order: x.order ?? idx };
  });

  return normalized
    .filter((x) => x && x.key)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
})();

  // Quill configs
  const richTextModules = {
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"],
      ],
    },
  };

  const policiesEditorModules = {
    toolbar: {
      container: [["bold"], [{ list: "bullet" }], ["clean"]],
    },
  };

  const handlePropertyNameChange = (content) => {
    setBranding((prev) => ({ ...prev, propertyName: content }));
  };

  const handlePropertyOverviewChange = (content) => {
    setBranding((prev) => ({ ...prev, propertyOverview: content }));
  };

  const getSection = (sectionKey, prev) => {
    const existing = prev[sectionKey];
    if (existing) return existing;
    switch (sectionKey) {
      case "policies":
        return createDefaultPoliciesSection();
      case "amenities":
        return createDefaultAmenitiesSection();
      case "contactLocation":
        return createDefaultContactSection();
      case "trustedPartners":
        return createDefaultPartnersSection();
      case "gallery":
        return createDefaultGallerySection();
      default:
        return existing || {};
    }
  };


    // ---- item helpers (amenities/policies) ----
  const sortByOrder = (items = []) =>
    items.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const normalizeOrder = (items = []) =>
    items.map((it, i) => ({ ...it, order: i }));

  const updateItemsSection = (sectionKey, updater) => {
    updateSection(sectionKey, (sec) => {
      const sorted = sortByOrder(sec.items || []);
      const nextSorted = updater(sorted) || sorted;
      return { ...sec, items: normalizeOrder(nextSorted) };
    });
  };

  const updateItemByKey = (sectionKey, key, patchOrFn) => {
    updateItemsSection(sectionKey, (items) =>
      items.map((it) => {
        if (it.key !== key) return it;
        return typeof patchOrFn === "function"
          ? patchOrFn(it)
          : { ...it, ...patchOrFn };
      })
    );
  };

  const removeItemByKey = (sectionKey, key) => {
    updateItemsSection(sectionKey, (items) => items.filter((it) => it.key !== key));
  };

  const moveItemByKey = (sectionKey, key, direction) => {
    updateItemsSection(sectionKey, (items) => {
      const idx = items.findIndex((it) => it.key === key);
      if (idx < 0) return items;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= items.length) return items;
      const arr = [...items];
      const tmp = arr[idx];
      arr[idx] = arr[newIdx];
      arr[newIdx] = tmp;
      return arr;
    });
  };


  const updateSection = (sectionKey, updater) => {
    setBranding((prev) => {
      const base = getSection(sectionKey, prev);
      const updated = updater(base);
      return { ...prev, [sectionKey]: updated };
    });
  };

  const policiesSection = branding.policies || createDefaultPoliciesSection();
  const amenitiesSection =
    branding.amenities || createDefaultAmenitiesSection();
  const contactSection =
    branding.contactLocation || createDefaultContactSection();
  const partnersSection =
    branding.trustedPartners || createDefaultPartnersSection();
  const gallerySection = branding.gallery || createDefaultGallerySection();

  // ============ Common header ============
  const renderSectionHeader = (sectionKey, section, label, badgeText) => (
    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <div className="d-flex flex-column flex-sm-row gap-2 align-items-start align-items-sm-center">
        <Form.Label className="mb-0 fw-semibold">{label}</Form.Label>
        {badgeText && (
          <Badge bg="light" text="dark">
            {badgeText}
          </Badge>
        )}
      </div>
      <Form.Check
        type="switch"
        id={`${sectionKey}-enabled`}
        label={
          section.enabled
            ? "Visible on booking engine"
            : "Hidden on booking engine"
        }
        checked={section.enabled}
        onChange={(e) =>
          updateSection(sectionKey, (sec) => ({
            ...sec,
            enabled: e.target.checked,
          }))
        }
      />
    </div>
  );

  // ============ Amenities ============
const amenitiesSorted = (amenitiesSection.items || [])
    .slice()
    .sort((a, b) => {
      const ao = typeof a.order === "number" ? a.order : 0;
      const bo = typeof b.order === "number" ? b.order : 0;
      return ao - bo;
    });

  const existingAmenityKeys = amenitiesSorted.map((a) => a.key);
  const availableDefaultAmenities = DEFAULT_AMENITIES.filter(
    (d) => !existingAmenityKeys.includes(d.key)
  );


const getDefaultAmenityByKey = (key) =>
  DEFAULT_AMENITIES.find((x) => String(x.key) === String(key)) || null;


const currentEditingAmenity = editingAmenityKey
  ? amenitiesSorted.find((a) => a.key === editingAmenityKey)
  : null;

const openAmenityEditor = (key) => {
  if (!key) return;

  const current = amenitiesSorted.find((x) => x.key === key) || null;
  const def = getDefaultAmenityByKey(key);

  setEditingAmenityKey(key);
  setAmenityDraft({
    // label fallback to default label if current is empty
    label: (current?.label ?? def?.label ?? "").toString(),
    // description fallback to defaultDescription if current is empty/null
    description: (current?.description ?? def?.defaultDescription ?? "").toString(),
  });

  setShowAmenityEditModal(true);
};

const closeAmenityEditor = () => {
  setShowAmenityEditModal(false);
  setEditingAmenityKey(null);
  setAmenityDraft({ label: "", description: "" });
};

const saveAmenityEditor = () => {
  if (!editingAmenityKey) return;

  updateItemByKey("amenities", editingAmenityKey, {
    label: String(amenityDraft.label || "").trim(),
    // do NOT trim aggressively; keep user formatting/newlines
    description: String(amenityDraft.description || ""),
  });

  closeAmenityEditor();
};

const resetAmenityToDefaultNote = () => {
  if (!editingAmenityKey) return;

  const def = getDefaultAmenityByKey(editingAmenityKey);
  const note = String(def?.defaultDescription || "");
  if (!note.trim()) return;

  setAmenityDraft((p) => ({ ...p, description: note }));
};



const handleAddDefaultAmenity = () => {
  if (!selectedDefaultAmenityKey) return;

  const def = getDefaultAmenityByKey(selectedDefaultAmenityKey);
  if (!def) return;

  updateSection("amenities", (sec) => {
    const items = [...(sec.items || [])];
    items.push({
      key: def.key,
      label: def.label,
      enabled: true,
      description: def.defaultDescription || "", // ✅ prefill
      source: "default",
      order: items.length,
    });
    return { ...sec, items };
  });

  setSelectedDefaultAmenityKey("");
};



  const handleAddCustomAmenity = () => {
    const name = (newAmenityName || "").trim();
    if (!name) return;

    updateSection("amenities", (sec) => {
      const items = [...(sec.items || [])];
      items.push({
        key: `custom-${Date.now()}`,
        label: name,
        enabled: true,
        description: "",
        source: "custom",
        order: items.length,
      });
      return { ...sec, items };
    });
    setNewAmenityName("");
  };

  const moveAmenity = (index, direction) => {
    updateSection("amenities", (sec) => {
      const items = amenitiesSorted;
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= items.length) return sec;
      const arr = [...items];
      const tmp = arr[index];
      arr[index] = arr[newIndex];
      arr[newIndex] = tmp;
      const reOrdered = arr.map((item, i) => ({ ...item, order: i }));
      return { ...sec, items: reOrdered };
    });
  };

  // ============ Policies ============
  const policiesSorted = (policiesSection.items || [])
    .slice()
    .sort((a, b) => {
      const ao = typeof a.order === "number" ? a.order : 0;
      const bo = typeof b.order === "number" ? b.order : 0;
      return ao - bo;
    });

  const existingPolicyKeys = policiesSorted.map((p) => p.key);
  const availableDefaultPolicies = DEFAULT_POLICIES.filter(
    (d) => !existingPolicyKeys.includes(d.key)
  );

  const handleAddDefaultPolicy = () => {
    if (!selectedDefaultPolicyKey) return;
    const def = DEFAULT_POLICIES.find(
      (d) => d.key === selectedDefaultPolicyKey
    );
    if (!def) return;

    updateSection("policies", (sec) => {
      const items = [...(sec.items || [])];
      items.push({
        ...def,
        enabled: true,
        description: def.defaultDescription,
        source: "default",
        order: items.length,
      });
      return { ...sec, items };
    });
    setSelectedDefaultPolicyKey("");
  };

  const handleAddCustomPolicy = () => {
    const name = (newPolicyName || "").trim();
    if (!name) return;

    updateSection("policies", (sec) => {
      const items = [...(sec.items || [])];
      items.push({
        key: `custom-${Date.now()}`,
        label: name,
        enabled: true,
        description: "",
        source: "custom",
        order: items.length,
      });
      return { ...sec, items };
    });
    setNewPolicyName("");
  };

const currentEditingPolicy = editingPolicyKey
  ? policiesSorted.find((p) => p.key === editingPolicyKey)
  : null;



  const openPolicyEditor = (policyKey) => {
  if (!policyKey) return;
  const p = policiesSorted.find((x) => x.key === policyKey);
  setEditingPolicyKey(policyKey);
  setPolicyDraftHtml(p?.description || "");
  setShowPolicyEditModal(true);
};

const closePolicyEditor = () => {
  setShowPolicyEditModal(false);
  setEditingPolicyKey(null);
  setPolicyDraftHtml("");
};

const savePolicyEditor = () => {
  if (!editingPolicyKey) return;
  updateItemByKey("policies", editingPolicyKey, { description: policyDraftHtml });
  closePolicyEditor();
};

  // ============ Partners ============
  const existingPartnerDefaultKeys = (partnersSection.partners || [])
    .filter((p) => p.source === "default")
    .map((p) => p.defaultKey);

  const availableDefaultPartners = DEFAULT_PARTNERS.filter(
    (d) => !existingPartnerDefaultKeys.includes(d.key)
  );

  // HERO: Upload
  const handleHeroImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHero(true);
    const formData = new FormData();
    formData.append("image", file);
    try {
      const res = await axios.post(
        `${apiUrl}/booking-engine/upload-image`,
        formData,
        authHeaders
      );
      const newKey = res.data.url.split("/").pop();
      setHeroImageKey(newKey);
    } catch (err) {
      alert("Hero image upload failed");
    } finally {
      setUploadingHero(false);
    }
  };

  // HERO: Delete
  const handleDeleteHeroImage = async () => {
    if (!heroImageKey || deletingHero) return;
    if (!window.confirm("Delete hero image permanently?")) return;
    setDeletingHero(true);
    try {
      await axios.delete(`${apiUrl}/booking-engine/hero-image`, authHeaders);
      setHeroImageKey("");
    } catch (err) {
      alert("Failed to delete hero image");
    } finally {
      setDeletingHero(false);
    }
  };

  // GALLERY: Upload (file -> DO Spaces, returns key)
  const handleGalleryUpload = async (file) => {
    if (!file) return null;
    setGalleryUploading(true);
    const formData = new FormData();
    formData.append("image", file);
    try {
      const res = await axios.post(
        `${apiUrl}/booking-engine/upload-gallery-image`,
        formData,
        authHeaders
      );
      return res.data.fileKey;
    } catch (err) {
      console.error(err);
      throw new Error("Upload failed");
    } finally {
      setGalleryUploading(false);
    }
  };

  // GALLERY: Delete
  const handleDeleteGalleryImage = async () => {
    const { key, idx } = confirmDelete;
    if (!key) return;

    const isExternal =
      key.startsWith("http://") || key.startsWith("https://");

    // Always remove from local branding config
    setGalleryDeleting((prev) => ({ ...prev, [idx]: true }));
    setBranding((prev) => ({
      ...prev,
      gallery: {
        ...prev.gallery,
        images: (prev.gallery?.images || []).filter(
          (img) => img.key !== key
        ),
      },
    }));

    // If this is an external URL, don't call backend delete API
    if (isExternal) {
      setGalleryDeleting((prev) => ({ ...prev, [idx]: false }));
      setConfirmDelete({ open: false, key: null, idx: null });
      return;
    }

    // For DO / CDN keys, call backend delete endpoint
    try {
      const response = await axios.delete(
        `${apiUrl}/booking-engine/gallery-image`,
        {
          data: { imageKey: key },
          ...authHeaders,
        }
      );

      const deletedKey = response.data.deletedKey || key;

      setBranding((prev) => ({
        ...prev,
        gallery: {
          ...prev.gallery,
          images: (prev.gallery?.images || []).filter(
            (img) => img.key !== deletedKey
          ),
        },
      }));
    } catch (err) {
      console.error(err);
    }

    setGalleryDeleting((prev) => ({ ...prev, [idx]: false }));
    setConfirmDelete({ open: false, key: null, idx: null });
  };

  const addPartner = () => {
    updateSection("trustedPartners", (sec) => ({
      ...sec,
      partners: [
        ...(sec.partners || []),
        {
          id: Date.now(),
          enabled: true,
          name: "",
          url: "",
          logoUrl: "",
          caption: "",
          source: "custom",
        },
      ],
    }));
  };

  const addDefaultPartner = () => {
    if (!selectedDefaultPartnerKey) return;
    const def = DEFAULT_PARTNERS.find(
      (d) => d.key === selectedDefaultPartnerKey
    );
    if (!def) return;

    updateSection("trustedPartners", (sec) => ({
      ...sec,
      partners: [
        ...(sec.partners || []),
        {
          id: Date.now(),
          enabled: true,
          name: def.name,
          url: def.url,
          logoUrl: def.logoUrl || "",
          caption: "",
          source: "default",
          defaultKey: def.key,
        },
      ],
    }));
    setSelectedDefaultPartnerKey("");
  };

  const updatePartnerField = (idx, field, value) => {
    updateSection("trustedPartners", (sec) => {
      const partners = (sec.partners || []).map((p, i) =>
        i === idx ? { ...p, [field]: value } : p
      );
      return { ...sec, partners };
    });
  };

  const removePartner = (idx) => {
    updateSection("trustedPartners", (sec) => ({
      ...sec,
      partners: (sec.partners || []).filter((_, i) => i !== idx),
    }));
  };

  const openAddGalleryModal = () => {
    setDraftImage({
      title: "",
      key: "",
      alt: "",
      tag: "",
      file: null,
      uploading: false,
      previewUrl: "",
      error: "",
    });
    setShowGalleryAddModal(true);
  };

  const closeAddGalleryModal = () => {
    if (draftImage.uploading) return;
    setShowGalleryAddModal(false);
  };

  const isImageKeyValid = (key) => {
    if (!key || !key.trim()) return false;
    const lowered = key.toLowerCase();
    if (lowered.startsWith("http://") || lowered.startsWith("https://"))
      return true;
    if (lowered.startsWith("data:")) return true; // data URL
    return (
      lowered.endsWith(".jpg") ||
      lowered.endsWith(".jpeg") ||
      lowered.endsWith(".png") ||
      lowered.endsWith(".webp") ||
      lowered.endsWith(".gif")
    );
  };

const getImageUrl = (imgOrDraft) => {
  if (!imgOrDraft) return "";

  // draft preview
  if (imgOrDraft.previewUrl) return imgOrDraft.previewUrl;

  // allow passing string directly
  const rawKey =
    typeof imgOrDraft === "string"
      ? imgOrDraft
      : (imgOrDraft.key || imgOrDraft.url || imgOrDraft.path || imgOrDraft.image || "");

  if (!rawKey) return "";

  const key = String(rawKey);
  const lowered = key.toLowerCase();

  // external URL or data uri
  if (
    lowered.startsWith("http://") ||
    lowered.startsWith("https://") ||
    lowered.startsWith("//") ||
    lowered.startsWith("data:")
  ) {
    return key;
  }

  // If no CDN base, make root-relative so "company_96/..." becomes "/company_96/..."
  const base = String(cdnBaseUrl || CDN_URL || "").replace(/\/$/, "");
  if (!base) return key.startsWith("/") ? key : `/${key}`;

  // CDN prefix
  return `${base}/${key.replace(/^\//, "")}`;
};


  const handleDraftFileChange = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const previewUrl = e.target.result;
      setDraftImage((prev) => ({
        ...prev,
        file,
        previewUrl,
        error: "",
      }));
    };
    reader.readAsDataURL(file);
  };

  // Add image from modal: upload OR external URL
  const addDraftImageToGallery = async () => {
    const hasManualKey = !!draftImage.key.trim();
    const hasFile = !!draftImage.file;

    if (!hasManualKey && !hasFile) {
      setDraftImage((prev) => ({
        ...prev,
        error:
          "Upload an image or provide a CDN key / URL to add this to the gallery.",
      }));
      return;
    }

    let keyToUse = draftImage.key.trim();
    let isExternalUrl = false;

    // CASE 1: user uploaded a file, but did NOT provide any key → upload to CDN
    if (!keyToUse && hasFile) {
      setDraftImage((prev) => ({
        ...prev,
        uploading: true,
        error: "",
      }));

      try {
        const fileKey = await handleGalleryUpload(draftImage.file); // calls /booking-engine/upload-gallery-image (file mode)
        keyToUse = fileKey; // DO key from backend
      } catch (err) {
        console.error("Gallery file upload failed", err);
        setDraftImage((prev) => ({
          ...prev,
          uploading: false,
          error: "Upload failed. Please try again.",
        }));
        return;
      } finally {
        // keep uploading=true until we finish rest of logic
      }
    }

    // CASE 2: user provided a manual key or URL
    // Decide if it is an external URL:
    if (keyToUse) {
      const lowered = keyToUse.toLowerCase();
      isExternalUrl =
        lowered.startsWith("http://") || lowered.startsWith("https://");
    }

    // If manual key is a full external URL, tell backend as well (externalUrl mode)
    if (isExternalUrl && !hasFile) {
      try {
        await axios.post(
          `${apiUrl}/booking-engine/upload-gallery-image`,
          { externalUrl: keyToUse },
          authHeaders
        );
        // backend stores this exact URL into gallery_images; we keep using keyToUse in frontend
      } catch (err) {
        console.error("Failed to register external gallery URL", err);
        // We won't block frontend UX; user can still see it.
      }
    }

    // Final validation (for both DO keys and URLs)
    if (!isImageKeyValid(keyToUse)) {
      setDraftImage((prev) => ({
        ...prev,
        uploading: false,
        error:
          "Please provide a valid image path or URL (.jpg, .jpeg, .png, .webp) or a data URL.",
      }));
      return;
    }

    const newImage = {
      id: Date.now(),
      enabled: true,
      title: draftImage.title.trim(),
      key: keyToUse,
      alt: draftImage.alt.trim(),
      tag: draftImage.tag.trim(),
      order: realGalleryImages.length,
    };

    updateSection("gallery", (sec) => {
      const images = [...(sec.images || []), newImage];
      return { ...sec, images };
    });

    // close and reset
    setShowGalleryAddModal(false);
    setDraftImage({
      title: "",
      key: "",
      alt: "",
      tag: "",
      file: null,
      uploading: false,
      previewUrl: "",
      error: "",
    });
  };


  // Update gallery item by index (index is on sorted array)
  const updateGalleryImageField = (idx, field, value) => {
    updateSection("gallery", (sec) => {
      const sorted = (sec.images || [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const updatedSorted = sorted.map((img, i) =>
        i === idx ? { ...img, [field]: value } : img
      );
      return {
        ...sec,
        images: updatedSorted.map((img, i) => ({ ...img, order: i })),
      };
    });
  };

  const openPreview = (img) => {
    const url = getImageUrl(img);
    if (!url) return;
    setPreviewImageUrl(url);
    setShowPreviewModal(true);
  };

  // =============================
  // RENDER
  // =============================
  return (
    <>
      <Card className="p-4 border-0 shadow-sm">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="fw-bold mb-0">Manage Booking Engine</h5>
            <small className="text-muted">
              Configure content shown in your booking widget.
            </small>
          </div>
          {onSave && (
            <Button variant="primary" onClick={onSave}>
              Save changes
            </Button>
          )}
        </div>

        {/* Inner tabs: Property Info content, Policies, Amenities, Contact, Partners, Gallery */}
        <Tabs defaultActiveKey="basic" className="mb-0">
          {/* PROPERTY INFO CONTENT */}
          <Tab
            eventKey="basic"
            title={
              <>
                <FaInfoCircle className="me-2" />
                Property Info
              </>
            }
          >
            <div className="pt-3">
              {/* ========================================== */}
              {/* NEW: SELECT PROPERTIES FOR BOOKING ENGINE */}
              {/* ========================================== */}
              <Card className="mb-4 border-0 shadow-sm" style={{ background: "#f8fafc" }}>
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <FaBuilding className="text-primary" size={20} />
                    <h6 className="fw-bold mb-0">Select Properties for Booking Engine</h6>
                  </div>

                  <Alert variant="info" className="mb-3 py-2">
                    <small>
                      <strong>Instructions:</strong> Select which properties should be displayed in the booking engine. 
                      You can select multiple properties. The currently active property (from your session) is highlighted below.
                      Your selection will be saved when you click "Save changes".
                    </small>
                  </Alert>

                  {loadingOrganizations ? (
                    <div className="d-flex align-items-center justify-content-center py-4">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <span className="text-muted">Loading properties...</span>
                    </div>
                  ) : organizationsError ? (
                    <Alert variant="danger" className="mb-0">
                      {organizationsError}
                      <Button
                        size="sm"
                        variant="outline-danger"
                        className="ms-3"
                        onClick={fetchOrganizations}
                      >
                        Retry
                      </Button>
                    </Alert>
                  ) : organizations.length === 0 ? (
                    <Alert variant="warning" className="mb-0">
                      No properties found. Please ensure you have properties configured in your account.
                    </Alert>
                  ) : (
                    <>
                      {/* Selection controls */}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="text-muted small">
                          <strong>{selectedProperties.length}</strong> of{" "}
                          <strong>{organizations.length}</strong> properties selected
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={selectAllProperties}
                            disabled={selectedProperties.length === organizations.length}
                          >
                            Select All
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={deselectAllProperties}
                            disabled={selectedProperties.length === 0}
                          >
                            Deselect All
                          </Button>
                        </div>
                      </div>

                      {/* Properties list */}
                      <div
                        style={{
                          maxHeight: "320px",
                          overflowY: "auto",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          background: "#fff",
                        }}
                      >
                        <Table hover className="mb-0" size="sm">
                          <thead style={{ position: "sticky", top: 0, background: "#f1f5f9", zIndex: 1 }}>
                            <tr>
                              <th style={{ width: "50px", textAlign: "center" }}>
                                <Form.Check
                                  type="checkbox"
                                  checked={
                                    organizations.length > 0 &&
                                    selectedProperties.length === organizations.length
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      selectAllProperties();
                                    } else {
                                      deselectAllProperties();
                                    }
                                  }}
                                  title="Select/Deselect All"
                                />
                              </th>
                              <th>Property Name</th>
                              <th style={{ width: "100px" }}>ID</th>
                              <th style={{ width: "120px" }}>Type</th>
                              <th style={{ width: "80px", textAlign: "center" }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {organizations.map((org) => {
                              const isSelected = isPropertySelected(org.company_id);
                              const isCurrent =
                                String(org.company_id) === String(currentCompanyId);

                              return (
                                <tr
                                  key={org.company_id}
                                  style={{
                                    background: isCurrent
                                      ? "#fffbeb"
                                      : isSelected
                                      ? "#f0fdf4"
                                      : "transparent",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => togglePropertySelection(org)}
                                >
                                  <td style={{ textAlign: "center" }}>
                                    <Form.Check
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => togglePropertySelection(org)}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="fw-medium">{org.company_name}</span>
                                      {isCurrent && (
                                        <Badge bg="warning" text="dark" className="ms-1">
                                          Current
                                        </Badge>
                                      )}
                                    </div>
                                    {org.company_email && (
                                      <small className="text-muted d-block">
                                        {org.company_email}
                                      </small>
                                    )}
                                  </td>
                                  <td>
                                    <code style={{ fontSize: "12px" }}>{org.company_id}</code>
                                  </td>
                                  <td>
                                    <Badge
                                      bg={
                                        org.type === "Parent"
                                          ? "primary"
                                          : org.type === "Child"
                                          ? "secondary"
                                          : "info"
                                      }
                                      className="text-uppercase"
                                      style={{ fontSize: "10px" }}
                                    >
                                      {org.type || "N/A"}
                                    </Badge>
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {isSelected ? (
                                      <FaCheckCircle className="text-success" size={16} />
                                    ) : (
                                      <span className="text-muted">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>

                      {/* Selected properties summary */}
                      {selectedProperties.length > 0 && (
                        <div className="mt-3 pt-3 border-top">
                          <div className="small fw-semibold text-muted mb-2">
                            Selected Properties:
                          </div>
                          <div className="d-flex flex-wrap gap-2">
                            {selectedProperties.map((p) => (
                              <Badge
                                key={p.company_id}
                                bg="primary"
                                className="d-flex align-items-center gap-1 py-1 px-2"
                                style={{ fontSize: "12px" }}
                              >
                                {p.company_name}
                                <span
                                  style={{ cursor: "pointer", marginLeft: "4px" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    togglePropertySelection(p);
                                  }}
                                  title="Remove"
                                >
                                  ×
                                </span>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
              {/* END: SELECT PROPERTIES FOR BOOKING ENGINE */}

              <Form.Group className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label>Property Name</Form.Label>
                  <Button
                    size="sm"
                    variant="light"
                    className="border"
                    onClick={() => setShowHtmlName(!showHtmlName)}
                  >
                    {showHtmlName ? "Editor View" : "HTML Source"}
                  </Button>
                </div>
                <small className="text-muted d-block mb-2">
                  This appears at the top of your booking widget.
                </small>

                {showHtmlName ? (
                  <textarea
                    value={branding.propertyName || ""}
                    onChange={(e) => handlePropertyNameChange(e.target.value)}
                    style={{
                      width: "100%",
                      height: "120px",
                      fontFamily: "monospace",
                      border: "1px solid #ccc",
                      padding: "10px",
                    }}
                  />
                ) : (
                  <ReactQuill
                    value={branding.propertyName || ""}
                    onChange={handlePropertyNameChange}
                    modules={richTextModules}
                    theme="snow"
                    placeholder="Enter property name..."
                  />
                )}
              </Form.Group>

              <Form.Group className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label>Property Overview</Form.Label>
                  <Button
                    size="sm"
                    variant="light"
                    className="border"
                    onClick={() => setShowHtmlOverview(!showHtmlOverview)}
                  >
                    {showHtmlOverview ? "Editor View" : "HTML Source"}
                  </Button>
                </div>
                <small className="text-muted d-block mb-2">
                  Short story about your property shown above search / rooms.
                </small>

                {showHtmlOverview ? (
                  <textarea
                    value={branding.propertyOverview || ""}
                    onChange={(e) =>
                      handlePropertyOverviewChange(e.target.value)
                    }
                    style={{
                      width: "100%",
                      height: "200px",
                      fontFamily: "monospace",
                      border: "1px solid #ccc",
                      padding: "10px",
                    }}
                  />
                ) : (
                  <ReactQuill
                    value={branding.propertyOverview || ""}
                    onChange={handlePropertyOverviewChange}
                    modules={richTextModules}
                    theme="snow"
                    placeholder="Enter property overview..."
                  />
                )}
              </Form.Group>
            </div>
          </Tab>
{/* POLICIES */}
<Tab
  eventKey="policies"
  title={
    <>
      <FaGavel className="me-2" />
      Policies
    </>
  }
>
  <div className="pt-3">
    {renderSectionHeader(
      "policies",
      policiesSection,
      "Policies",
      "Guests see this before confirming"
    )}

    {!policiesSection.enabled ? (
      <div className="text-muted small">
        Turn this section <strong>On</strong> to manage and show policies.
      </div>
    ) : (
      <>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Section Title</Form.Label>
              <Form.Control
                type="text"
                value={policiesSection.title || "Policies"}
                onChange={(e) =>
                  updateSection("policies", (sec) => ({
                    ...sec,
                    title: e.target.value,
                  }))
                }
                placeholder="e.g. Hotel Policies"
              />
            </Form.Group>
          </Col>
        </Row>

        <Card className="mb-3 border-0 shadow-sm">
          <Card.Body>
            <div className="mb-2 fw-semibold">Quick add policies</div>
            <Row className="gy-2">
              <Col md={6}>
                <Form.Label className="small">Add from common policies</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Select
                    size="sm"
                    value={selectedDefaultPolicyKey}
                    onChange={(e) => setSelectedDefaultPolicyKey(e.target.value)}
                  >
                    <option value="">Choose a policy...</option>
                    {availableDefaultPolicies.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={!selectedDefaultPolicyKey}
                    onClick={handleAddDefaultPolicy}
                  >
                    Add
                  </Button>
                </div>
                <small className="text-muted">We list the most used hotel policies here.</small>
              </Col>

              <Col md={6}>
                <Form.Label className="small">Add custom policy</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="e.g. Local Rules"
                    value={newPolicyName}
                    onChange={(e) => setNewPolicyName(e.target.value)}
                  />
                  <Button
                    size="sm"
                    variant="outline-success"
                    disabled={!newPolicyName.trim()}
                    onClick={handleAddCustomPolicy}
                  >
                    Add
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="mb-2 fw-semibold">Your policies</div>
        {policiesSorted.length === 0 ? (
          <div className="text-muted small mb-2">
            You have not added any policy yet. Use the controls above to pick from common
            policies or add your own.
          </div>
        ) : (
          <small className="text-muted d-block mb-2">
            Toggle <strong>Show</strong>, rename, then click <strong>Edit text</strong> to
            update the bullet points.
          </small>
        )}

        {policiesSorted.length === 0 ? null : (
          <Table hover responsive size="sm" className="align-middle">
            <thead>
              <tr>
                <th style={{ width: "5rem" }}>Show</th>
                <th>Name</th>
                <th style={{ width: "9rem" }}>Type</th>
                <th style={{ width: "7rem" }}>Text</th>
                <th style={{ width: "4rem" }}></th>
              </tr>
            </thead>
            <tbody>
              {policiesSorted.map((p, idx) => {
                // key MUST exist for safe updates; your add handlers already set it.
                const k = p.key;

                return (
                  <tr key={k || idx}>
                    <td>
                      <Form.Check
                        type="switch"
                        id={`policy-${k || idx}`}
                        checked={!!p.enabled}
                        onChange={(e) => {
                          if (!k) return;
                          updateItemByKey("policies", k, { enabled: e.target.checked });
                        }}
                        label=""
                      />
                    </td>

                    <td>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={p.label || ""}
                        onChange={(e) => {
                          if (!k) return;
                          updateItemByKey("policies", k, { label: e.target.value });
                        }}
                      />
                    </td>

                    <td>
                      <Badge bg="light" text="dark">
                        {p.source === "custom" ? "Custom" : "Default"}
                      </Badge>
                    </td>

                    <td>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => {
                          if (!k) return;
                          openPolicyEditor(k);
                        }}

                      >
                        Edit text
                      </Button>
                    </td>

                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => {
                          if (!k) return;
                          if (editingPolicyKey === k) closePolicyEditor();
                          removeItemByKey("policies", k);

                        }}
                      >
                        ✕
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}


      </>
    )}
  </div>
</Tab>

{/* AMENITIES */}
<Tab
  eventKey="amenities"
  title={
    <>
      <FaConciergeBell className="me-2" />
      Amenities & Facilities
    </>
  }
>
  <div className="pt-3">
    {renderSectionHeader(
      "amenities",
      amenitiesSection,
      "Amenities & Facilities",
      "Highlights shown to guests"
    )}

    {!amenitiesSection.enabled ? (
      <div className="text-muted small">
        Turn this section <strong>On</strong> to manage and show amenities.
      </div>
    ) : (
      <>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Section Title</Form.Label>
              <Form.Control
                type="text"
                value={amenitiesSection.title || "Amenities & Facilities"}
                onChange={(e) =>
                  updateSection("amenities", (sec) => ({
                    ...sec,
                    title: e.target.value,
                  }))
                }
                placeholder="e.g. Amenities & Facilities"
              />
            </Form.Group>
          </Col>
        </Row>

        <Card className="mb-3 border-0 shadow-sm">
          <Card.Body>
            <div className="mb-2 fw-semibold">Quick add amenities</div>
            <Row className="gy-2">
              <Col md={6}>
                <Form.Label className="small">Add from common amenities</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Select
                    size="sm"
                    value={selectedDefaultAmenityKey}
                    onChange={(e) => setSelectedDefaultAmenityKey(e.target.value)}
                  >
                    <option value="">Choose an amenity...</option>
                    {availableDefaultAmenities.map((a) => (
                      <option key={a.key} value={a.key}>
                        {a.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={!selectedDefaultAmenityKey}
                    onClick={handleAddDefaultAmenity}
                  >
                    Add
                  </Button>
                </div>
                <small className="text-muted">We've added the most used amenities here.</small>
              </Col>

              <Col md={6}>
                <Form.Label className="small">Add custom amenity</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="e.g. Rooftop Bar"
                    value={newAmenityName}
                    onChange={(e) => setNewAmenityName(e.target.value)}
                  />
                  <Button
                    size="sm"
                    variant="outline-success"
                    disabled={!newAmenityName.trim()}
                    onClick={handleAddCustomAmenity}
                  >
                    Add
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="mb-2 fw-semibold">Your amenities</div>
        {amenitiesSorted.length === 0 ? (
          <div className="text-muted small mb-2">
            You have not added any amenities yet. Use the quick add or create your own.
          </div>
        ) : (
          <small className="text-muted d-block mb-2">
            Toggle <strong>Show</strong> to control what appears on the booking engine. Change
            the order using arrows.
          </small>
        )}

        {amenitiesSorted.length === 0 ? null : (
<Table hover responsive size="sm" className="align-middle">
  <thead>
    <tr>
      <th style={{ width: "5rem" }}>Show</th>
      <th>Name</th>
      <th style={{ width: "35%" }}>Short note (optional)</th>
      <th style={{ width: "6rem" }}>Type</th>
      <th style={{ width: "80px" }}>Order</th>
      <th style={{ width: "4rem" }}></th>
    </tr>
  </thead>

  <tbody>
    {amenitiesSorted.map((a, idx) => {
      const k = a?.key ? String(a.key) : "";
      const isDefault = a?.source !== "custom";
      const def = k ? getDefaultAmenityByKey(k) : null;
      const hasDefaultNote = !!(def?.defaultDescription && String(def.defaultDescription).trim());

      const note = String(a?.description || "").trim();
      const notePreview =
        note.length > 90 ? `${note.slice(0, 90)}…` : note;

      const isFirst = idx === 0;
      const isLast = idx === amenitiesSorted.length - 1;

      // If something is wrong and key is missing, don't render a broken row.
      // (Your add handlers already ensure key exists, so this is just defensive.)
      if (!k) return null;

      return (
        <tr key={k}>
          {/* Show */}
          <td>
            <Form.Check
              type="switch"
              id={`amenity-${k}`}
              checked={!!a.enabled}
              onChange={(e) => updateItemByKey("amenities", k, { enabled: e.target.checked })}
              label=""
              aria-label={`Toggle amenity ${a.label || k}`}
            />
          </td>

          {/* Name */}
          <td>
            <Form.Control
              size="sm"
              type="text"
              value={a.label || ""}
              onChange={(e) => updateItemByKey("amenities", k, { label: e.target.value })}
              placeholder={isDefault ? def?.label || "Amenity name" : "Amenity name"}
            />
          </td>

          {/* Note (modal edit) */}
          <td>
            <div className="d-flex align-items-start justify-content-between gap-2">
              <div className="flex-grow-1">
                {note ? (
                  <div className="small text-muted" style={{ lineHeight: 1.25 }}>
                    {notePreview}
                  </div>
                ) : (
                  <div className="small text-muted fst-italic">No note</div>
                )}
              </div>

              <div className="d-flex gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => openAmenityEditor(k)}
                  aria-label={`Edit note for ${a.label || "amenity"}`}
                >
                  Edit note
                </Button>

                {hasDefaultNote && (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => {
                      // open editor; reset happens inside modal OR you can do direct reset here
                      openAmenityEditor(k);
                    }}
                    aria-label={`View default note for ${a.label || "amenity"}`}
                    title="Default note available"
                  >
                    Default
                  </Button>
                )}
              </div>
            </div>
          </td>

          {/* Type */}
          <td>
            <Badge bg="light" text="dark">
              {isDefault ? "Default" : "Custom"}
            </Badge>
          </td>

          {/* Order */}
          <td>
            <div className="d-flex flex-column gap-1">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={isFirst}
                onClick={() => moveItemByKey("amenities", k, -1)}
                aria-label={`Move ${a.label || "amenity"} up`}
              >
                ↑
              </Button>

              <Button
                size="sm"
                variant="outline-secondary"
                disabled={isLast}
                onClick={() => moveItemByKey("amenities", k, +1)}
                aria-label={`Move ${a.label || "amenity"} down`}
              >
                ↓
              </Button>
            </div>
          </td>

          {/* Delete */}
          <td className="text-end">
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => {
                if (editingAmenityKey === k) closeAmenityEditor();
                removeItemByKey("amenities", k);
              }}
              aria-label={`Remove ${a.label || "amenity"}`}
              title="Remove"
            >
              ✕
            </Button>
          </td>
        </tr>
      );
    })}
  </tbody>
</Table>

        )}
      </>
    )}
  </div>
</Tab>


          {/* CONTACT & LOCATION */}
          <Tab
            eventKey="contact"
            title={
              <>
                <FaMapMarkerAlt className="me-2" />
                Contact & Location
              </>
            }
          >
            <div className="pt-3">
              {renderSectionHeader(
                "contactLocation",
                contactSection,
                "Contact & Location",
                "Used for directions & support"
              )}

              {!contactSection.enabled ? (
                <div className="text-muted small">
                  Turn this section <strong>On</strong> to show contact and
                  location details.
                </div>
              ) : (
                <>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Section Title</Form.Label>
                        <Form.Control
                          type="text"
                          value={
                            contactSection.title || "Contact & Location"
                          }
                          onChange={(e) =>
                            updateSection("contactLocation", (sec) => ({
                              ...sec,
                              title: e.target.value,
                            }))
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="text"
                          value={contactSection.phone || ""}
                          onChange={(e) =>
                            updateSection("contactLocation", (sec) => ({
                              ...sec,
                              phone: e.target.value,
                            }))
                          }
                          placeholder="+91 ..."
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={contactSection.email || ""}
                          onChange={(e) =>
                            updateSection("contactLocation", (sec) => ({
                              ...sec,
                              email: e.target.value,
                            }))
                          }
                          placeholder="reservations@example.com"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>WhatsApp</Form.Label>
                        <Form.Control
                          type="text"
                          value={contactSection.whatsapp || ""}
                          onChange={(e) =>
                            updateSection("contactLocation", (sec) => ({
                              ...sec,
                              whatsapp: e.target.value,
                            }))
                          }
                          placeholder="+91 ..."
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Website</Form.Label>
                        <Form.Control
                          type="text"
                          value={contactSection.website || ""}
                          onChange={(e) =>
                            updateSection("contactLocation", (sec) => ({
                              ...sec,
                              website: e.target.value,
                            }))
                          }
                          placeholder="https://yourhotel.com"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Address Line 1</Form.Label>
                        <Form.Control
                          type="text"
                          value={contactSection.addressLine1 || ""}
                          onChange={(e) =>
                            updateSection("contactLocation", (sec) => ({
                              ...sec,
                              addressLine1: e.target.value,
                            }))
                          }
                          placeholder="Street, Building"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Address Line 2</Form.Label>
                        <Form.Control
                          type="text"
                          value={contactSection.addressLine2 || ""}
                          onChange={(e) =>
                            updateSection("contactLocation", (sec) => ({
                              ...sec,
                              addressLine2: e.target.value,
                            }))
                          }
                          placeholder="Area, Landmark"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          value={contactSection.city || ""}
                          onChange={(e) =>
                            updateSection("contactLocation", (sec) => ({
                              ...sec,
                              city: e.target.value,
                            }))
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>State</Form.Label>
                        <Form.Control
                          type="text"
                          value={contactSection.state || ""}
                          onChange={(e) =>
                            updateSection("contactLocation", (sec) => ({
                              ...sec,
                              state: e.target.value,
                            }))
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Country</Form.Label>
                        <Form.Control
                          type="text"
                          value={contactSection.country || ""}
                          onChange={(e) =>
                            updateSection("contactLocation", (sec) => ({
                              ...sec,
                              country: e.target.value,
                            }))
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Postcode / ZIP</Form.Label>
                        <Form.Control
                          type="text"
                          value={contactSection.postcode || ""}
                          onChange={(e) =>
                            updateSection("contactLocation", (sec) => ({
                              ...sec,
                              postcode: e.target.value,
                            }))
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Latitude</Form.Label>
                        <Form.Control
                          type="text"
                          value={contactSection.latitude || ""}
                          onChange={(e) =>
                            updateSection("contactLocation", (sec) => ({
                              ...sec,
                              latitude: e.target.value,
                            }))
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Longitude</Form.Label>
                        <Form.Control
                          type="text"
                          value={contactSection.longitude || ""}
                          onChange={(e) =>
                            updateSection("contactLocation", (sec) => ({
                              ...sec,
                              longitude: e.target.value,
                            }))
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Map Embed URL (optional)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={contactSection.mapEmbedUrl || ""}
                          onChange={(e) =>
                            updateSection("contactLocation", (sec) => ({
                              ...sec,
                              mapEmbedUrl: e.target.value,
                            }))
                          }
                          placeholder="Paste Google Maps embed URL"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}
            </div>
          </Tab>

          {/* TRUSTED PARTNERS */}
          <Tab
            eventKey="partners"
            title={
              <>
                <FaHandshake className="me-2" />
                Trusted Partners
              </>
            }
          >
            <div className="pt-3">
              {renderSectionHeader(
                "trustedPartners",
                partnersSection,
                "Trusted Partners",
                "Logos & OTA partners"
              )}

              {!partnersSection.enabled ? (
                <div className="text-muted small">
                  Turn this section <strong>On</strong> to manage and show
                  partners.
                </div>
              ) : (
                <>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Section Title</Form.Label>
                        <Form.Control
                          type="text"
                          value={partnersSection.title || "Trusted Partners"}
                          onChange={(e) =>
                            updateSection("trustedPartners", (sec) => ({
                              ...sec,
                              title: e.target.value,
                            }))
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Card className="mb-3 border-0 shadow-sm">
                    <Card.Body>
                      <div className="mb-2 fw-semibold">
                        Quick add partners
                      </div>
                      <Row className="gy-2">
                        <Col md={6}>
                          <Form.Label className="small">
                            Add from common OTAs
                          </Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Select
                              size="sm"
                              value={selectedDefaultPartnerKey}
                              onChange={(e) =>
                                setSelectedDefaultPartnerKey(e.target.value)
                              }
                            >
                              <option value="">Choose a partner...</option>
                              {availableDefaultPartners.map((p) => (
                                <option key={p.key} value={p.key}>
                                  {p.name}
                                </option>
                              ))}
                            </Form.Select>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={!selectedDefaultPartnerKey}
                              onClick={addDefaultPartner}
                            >
                              Add
                            </Button>
                          </div>
                        </Col>
                        <Col md={6}>
                          <Form.Label className="small">
                            Add custom partner
                          </Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Control
                              size="sm"
                              type="text"
                              placeholder="Partner name"
                              value={newPartnerName}
                              onChange={(e) =>
                                setNewPartnerName(e.target.value)
                              }
                            />
                            <Button
                              size="sm"
                              variant="outline-success"
                              disabled={!newPartnerName.trim()}
                              onClick={() => {
                                updateSection("trustedPartners", (sec) => ({
                                  ...sec,
                                  partners: [
                                    ...(sec.partners || []),
                                    {
                                      id: Date.now(),
                                      enabled: true,
                                      name: newPartnerName.trim(),
                                      url: "",
                                      logoUrl: "",
                                      caption: "",
                                      source: "custom",
                                    },
                                  ],
                                }));
                                setNewPartnerName("");
                              }}
                            >
                              Add
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="fw-semibold">Partner list</div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={addPartner}
                    >
                      Add Row
                    </Button>
                  </div>

                  {(partnersSection.partners || []).length === 0 ? (
                    <div className="text-muted small">
                      You have not added any partners yet. Use quick add for
                      OTAs or add your own partner row.
                    </div>
                  ) : (
                    <Table
                      hover
                      responsive
                      size="sm"
                      className="align-middle"
                    >
                      <thead>
                        <tr>
                          <th style={{ width: "5rem" }}>Show</th>
                          <th style={{ width: "18%" }}>Name</th>
                          <th style={{ width: "22%" }}>Website</th>
                          <th style={{ width: "22%" }}>Logo URL</th>
                          <th>Caption (optional)</th>
                          <th style={{ width: "7rem" }}>Type</th>
                          <th style={{ width: "4rem" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {partnersSection.partners.map((p, idx) => (
                          <tr key={p.id || idx}>
                            <td>
                              <Form.Check
                                type="switch"
                                id={`partner-${idx}-enabled`}
                                checked={p.enabled}
                                onChange={(e) =>
                                  updatePartnerField(
                                    idx,
                                    "enabled",
                                    e.target.checked
                                  )
                                }
                                label=""
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Booking.com"
                                value={p.name || ""}
                                onChange={(e) =>
                                  updatePartnerField(
                                    idx,
                                    "name",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="https://..."
                                value={p.url || ""}
                                onChange={(e) =>
                                  updatePartnerField(
                                    idx,
                                    "url",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="https://cdn.../logo.png"
                                value={p.logoUrl || ""}
                                onChange={(e) =>
                                  updatePartnerField(
                                    idx,
                                    "logoUrl",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                as="textarea"
                                rows={1}
                                placeholder="e.g. Official distribution partner"
                                value={p.caption || ""}
                                onChange={(e) =>
                                  updatePartnerField(
                                    idx,
                                    "caption",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td>
                              <Badge bg="light" text="dark">
                                {p.source === "default" ? "Default" : "Custom"}
                              </Badge>
                            </td>
                            <td className="text-end">
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => removePartner(idx)}
                              >
                                ✕
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </>
              )}
            </div>
          </Tab>

          {/* GALLERY */}
          <Tab
            eventKey="gallery"
            title={
              <>
                <FaImages className="me-2" /> Gallery
              </>
            }
          >
            <div className="pt-3">
              {renderSectionHeader(
                "gallery",
                gallerySection,
                "Gallery",
                "Images guests see in the booking engine"
              )}


  
              {!gallerySection.enabled ? (
                <Alert variant="info">
                  Turn this section <strong>On</strong> to show gallery images.
                </Alert>
              ) : (
                <>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          value={gallerySection.title || "Gallery"}
                          onChange={(e) =>
                            updateSection("gallery", (s) => ({
                              ...s,
                              title: e.target.value,
                            }))
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Subtitle (optional)</Form.Label>
                        <Form.Control
                          value={gallerySection.subtitle || ""}
                          onChange={(e) =>
                            updateSection("gallery", (s) => ({
                              ...s,
                              subtitle: e.target.value,
                            }))
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Card className="border-0 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6>Gallery Images ({realGalleryImages.length})</h6>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={openAddGalleryModal}
                        >
                          Add Image
                        </Button>
                      </div>

                      {realGalleryImages.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                          <FaImages size={48} className="mb-3 opacity-25" />
                          <p>No images yet. Click &quot;Add Image&quot;.</p>
                        </div>
                      ) : (
                        <Row xs={2} md={3} lg={4} className="g-4">
                          {realGalleryImages.map((img, idx) => (
                            <Col key={img.id || img.key || idx}>
                              <Card className="h-100 shadow-sm border-0">
                                <img
                                  src={getImageUrl(img)}
                                  alt={img.alt || img.title || ""}
                                  className="card-img-top"
                                  style={{
                                    height: "160px",
                                    objectFit: "cover",
                                  }}
                                />
                                <Card.Body className="p-2 d-flex justify-content-center gap-2">
                                  <Button
                                    variant="light"
                                    size="sm"
                                    onClick={() => openPreview(img)}
                                    title="View Image"
                                  >
                                    <FaEye />
                                  </Button>

                                  <Button
                                    variant="light"
                                    size="sm"
                                    onClick={() => {
                                      setEditImage({ ...img, idx });
                                      setShowEditModal(true);
                                    }}
                                    title="Edit Image"
                                  >
                                    <FaEdit />
                                  </Button>

                                  <Button
                                    variant="light"
                                    size="sm"
                                    className="text-danger"
                                    onClick={() =>
                                      setConfirmDelete({
                                        open: true,
                                        key: img.key,
                                        idx,
                                      })
                                    }
                                    disabled={galleryDeleting[idx]}
                                    title="Delete Image"
                                  >
                                    <FaTrashAlt />
                                  </Button>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </Card.Body>
                  </Card>
                </>
              )}
            </div>
          </Tab>

          {/* ATTRACTIONS */}
          <Tab
            eventKey="attractions"
            title={
              <>
                <FaLandmark className="me-2" />
                Attractions
              </>
            }
          >
            <AttractionsEditor
              branding={branding}
              setBranding={setBranding}
            />
          </Tab>

          {/* PRIVACY POLICY */}
          <Tab
            eventKey="privacyPolicy"
            title={
              <>
                <FaShieldAlt className="me-2" />
                Privacy Policy
              </>
            }
          >
            <PrivacyPolicyEditor
              branding={branding}
              setBranding={setBranding}
            />
          </Tab>
        </Tabs>
      </Card>

      {/* full image preview */}
      <Modal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        size="lg"
        centered
      >
        <Modal.Body className="p-0">
          {previewImageUrl && (
            <img
              src={previewImageUrl}
              alt="Preview"
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Gallery Add Image modal */}
      <Modal
        show={showGalleryAddModal}
        onHide={closeAddGalleryModal}
        centered
        size="lg"
        dialogClassName="gallery-upload-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add gallery image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="gy-3">
            <Col md={5}>
              <Form.Label className="small">Image preview</Form.Label>
              <div
                style={{
                  border: "1px dashed #d1d5db",
                  borderRadius: 8,
                  padding: 12,
                  textAlign: "center",
                  height: "100%",
                }}
              >
                {getImageUrl(draftImage) ? (
                  <img
                    src={getImageUrl(draftImage)}
                    alt={draftImage.alt || draftImage.title || ""}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "220px",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div className="text-muted small" style={{ minHeight: 120 }}>
                    No image selected yet.
                  </div>
                )}

                <Form.Label
                  className="btn btn-outline-primary btn-sm mt-2 mb-0"
                  style={{
                    cursor: draftImage.uploading ? "not-allowed" : "pointer",
                  }}
                >
                  {draftImage.uploading ? "Uploading..." : "Choose image file"}
                  <Form.Control
                    type="file"
                    accept="image/*"
                    hidden
                    disabled={draftImage.uploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleDraftFileChange(file);
                    }}
                  />
                </Form.Label>
                <div className="text-muted small mt-2">
                  <strong>Tip:</strong> Upload if the file is on your computer.
                  If it&apos;s already on your CDN or another host, just paste
                  the key or full URL on the right instead.
                </div>
              </div>
            </Col>
            <Col md={7}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Title <span className="text-muted">(optional)</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Shown as caption in some layouts"
                  value={draftImage.title}
                  onChange={(e) =>
                    setDraftImage((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                />
              </Form.Group>

              <Form.Group className="mb-1">
                <Form.Label>
                  CDN key / full URL{" "}
                  <span className="text-muted">(optional)</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Key or full URL (e.g. company_61/.../gallery_123.jpg or https://.../image.webp)"
                  value={draftImage.key}
                  onChange={(e) =>
                    setDraftImage((prev) => ({
                      ...prev,
                      key: e.target.value,
                    }))
                  }
                />
              </Form.Group>
              <small className="text-muted d-block mb-3">
                You can either <strong>upload a file</strong> or{" "}
                <strong>paste a CDN / external URL</strong>. If you upload and
                leave this blank, we&apos;ll use the key returned by your
                upload API.
              </small>

              <Form.Group className="mb-3">
                <Form.Label>
                  Alt text <span className="text-muted">(optional)</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Shown if image fails to load"
                  value={draftImage.alt}
                  onChange={(e) =>
                    setDraftImage((prev) => ({
                      ...prev,
                      alt: e.target.value,
                    }))
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Tag <span className="text-muted">(optional)</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Rooms, Lobby, Pool"
                  value={draftImage.tag}
                  onChange={(e) =>
                    setDraftImage((prev) => ({
                      ...prev,
                      tag: e.target.value,
                    }))
                  }
                />
              </Form.Group>

              {draftImage.error && (
                <div className="text-danger small mt-1">
                  {draftImage.error}
                </div>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeAddGalleryModal}
            disabled={draftImage.uploading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={addDraftImageToGallery}
            disabled={draftImage.uploading}
          >
            Add to gallery
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        show={confirmDelete.open}
        onHide={() =>
          setConfirmDelete({ open: false, key: null, idx: null })
        }
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger fw-bold">
            Delete Image?
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="text-center">
          <p className="mb-3">
            Are you sure you want to delete this gallery image?
          </p>
          {confirmDelete.key && (
              <img
                src={getImageUrl(editImage)}
                alt={editImage.alt || ""}
                style={{
                  maxWidth: "100%",
                  maxHeight: "220px",
                  objectFit: "cover",
                }}
              />

          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setConfirmDelete({ open: false, key: null, idx: null })
            }
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteGalleryImage}>
            Delete Permanently
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit image modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size="lg"
        dialogClassName="gallery-upload-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit gallery image</Modal.Title>
        </Modal.Header>

        {editImage && (
          <Modal.Body>
            <Row className="gy-3">
              <Col md={5}>
                <Form.Label className="small">Current Image</Form.Label>
                <div
                  style={{
                    border: "1px dashed #d1d5db",
                    borderRadius: 8,
                    padding: 12,
                    textAlign: "center",
                  }}
                >
                  <img
                    src={getImageUrl(editImage)}
                    alt={editImage.alt || ""}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "220px",
                      objectFit: "cover",
                    }}
                  />

                  <div className="mt-2 text-muted small">
                    To change the image file itself, upload a new one below.
                  </div>

                  <Form.Label className="btn btn-outline-primary btn-sm mt-2 mb-0">
                    Replace image
                    <Form.Control
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        try {
                          const newKey = await handleGalleryUpload(file);
                          if (!newKey) return;
                          // update state + main gallery
                          updateGalleryImageField(editImage.idx, "key", newKey);
                          setEditImage((prev) => ({
                            ...prev,
                            key: newKey,
                          }));
                        } catch (err) {
                          alert("Failed to replace image.");
                        }
                      }}
                    />
                  </Form.Label>
                </div>
              </Col>

              <Col md={7}>
                <Form.Group className="mb-3">
                  <Form.Label>Title (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={editImage.title || ""}
                    onChange={(e) =>
                      setEditImage((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Alt text (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={editImage.alt || ""}
                    onChange={(e) =>
                      setEditImage((prev) => ({
                        ...prev,
                        alt: e.target.value,
                      }))
                    }
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Tag (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={editImage.tag || ""}
                    onChange={(e) =>
                      setEditImage((prev) => ({
                        ...prev,
                        tag: e.target.value,
                      }))
                    }
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Image key / URL (advanced)</Form.Label>
                  <Form.Control
                    type="text"
                    value={editImage.key || ""}
                    onChange={(e) =>
                      setEditImage((prev) => ({
                        ...prev,
                        key: e.target.value,
                      }))
                    }
                  />
                  <small className="text-muted">
                    You can also directly change the stored key or full URL
                    here. Make sure it points to a valid image.
                  </small>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
        )}

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowEditModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (!editImage) return;
              updateGalleryImageField(editImage.idx, "title", editImage.title);
              updateGalleryImageField(editImage.idx, "alt", editImage.alt);
              updateGalleryImageField(editImage.idx, "tag", editImage.tag);
              updateGalleryImageField(editImage.idx, "key", editImage.key);
              setShowEditModal(false);
            }}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>



      {/* ✅ Policy editor modal */}
<Modal
  show={showPolicyEditModal}
  onHide={closePolicyEditor}
  centered
  size="xl"
  scrollable
  backdrop="static"
  dialogClassName="policy-editor-modal"
>
  <Modal.Header closeButton>
    <Modal.Title>
      Edit policy text{currentEditingPolicy?.label ? `: ${currentEditingPolicy.label}` : ""}
    </Modal.Title>
  </Modal.Header>

  <Modal.Body>
    <Row className="gy-3">
      <Col lg={7}>
        <div className="fw-semibold mb-2">Editor</div>
        <div className="text-muted small mb-2">
          Allowed formatting: <strong>bold</strong> + bullet points only.
        </div>

        <ReactQuill
          value={policyDraftHtml}
          onChange={setPolicyDraftHtml}
          modules={policiesEditorModules}
          theme="snow"
          placeholder="Add bullet points for this policy..."
        />
      </Col>

      <Col lg={5}>
        <div className="fw-semibold mb-2">Preview</div>
        <Card className="border-0 shadow-sm">
          <Card.Body style={{ maxHeight: 380, overflow: "auto" }}>
            {policyDraftHtml?.trim() ? (
              <div dangerouslySetInnerHTML={{ __html: policyDraftHtml }} />
            ) : (
              <div className="text-muted small">Nothing to preview yet.</div>
            )}
          </Card.Body>
        </Card>

        <Alert variant="light" className="mt-3 mb-0 small">
          Tip: Keep bullets short. Guests scan policies quickly.
        </Alert>
      </Col>
    </Row>
  </Modal.Body>

  <Modal.Footer>
    <Button variant="outline-secondary" onClick={closePolicyEditor}>
      Cancel
    </Button>
    <Button variant="primary" onClick={savePolicyEditor}>
      Save
    </Button>
  </Modal.Footer>
</Modal>
{/* ✅ Amenity editor modal */}
<Modal
  show={showAmenityEditModal}
  onHide={closeAmenityEditor}
  centered
  size="lg"
  scrollable
  backdrop="static"
>
  <Modal.Header closeButton>
    <Modal.Title>
      Edit amenity{currentEditingAmenity?.label ? `: ${currentEditingAmenity.label}` : ""}
    </Modal.Title>
  </Modal.Header>

  <Modal.Body>
    <Row className="gy-3">
      <Col md={7}>
        <Form.Group className="mb-3">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            value={amenityDraft.label}
            onChange={(e) => setAmenityDraft((p) => ({ ...p, label: e.target.value }))}
            placeholder="Amenity name..."
          />
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>Note / description</Form.Label>
          <Form.Control
            as="textarea"
            rows={6}
            value={amenityDraft.description}
            onChange={(e) => setAmenityDraft((p) => ({ ...p, description: e.target.value }))}
            placeholder="Short note guests will see..."
          />

          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">Keep it short. Guests scan quickly.</small>

            {(() => {
              const def = editingAmenityKey ? getDefaultAmenityByKey(editingAmenityKey) : null;
              return def?.defaultDescription ? (
                <Button size="sm" variant="outline-primary" onClick={resetAmenityToDefaultNote}>
                  Reset to default note
                </Button>
              ) : null;
            })()}
          </div>
        </Form.Group>
      </Col>

      <Col md={5}>
        <div className="fw-semibold mb-2">Preview</div>
        <Card className="border-0 shadow-sm">
          <Card.Body style={{ maxHeight: 340, overflow: "auto" }}>
            <div className="fw-semibold">
              {amenityDraft.label?.trim() ? amenityDraft.label : "Amenity name"}
            </div>
            <div className="text-muted small mt-2" style={{ whiteSpace: "pre-wrap" }}>
              {amenityDraft.description?.trim() ? amenityDraft.description : "No note"}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>

    <Alert variant="light" className="mt-3 mb-0 small">
      Tip: Keep notes crisp (1–2 lines). Use details only when it prevents confusion.
    </Alert>
  </Modal.Body>

  <Modal.Footer>
    <Button variant="outline-secondary" onClick={closeAmenityEditor}>
      Cancel
    </Button>
    <Button variant="primary" onClick={saveAmenityEditor}>
      Save
    </Button>
  </Modal.Footer>
</Modal>



    </>
  );
};

export default PropertyContentEditor;